/** The itinerary's rules and the server's messages about it, kept for the whole page
 *  (plan §5): mounted by the tour page for a TOUR-type tour on every tab, so an invalid
 *  itinerary blocks Save — with the reason on its input and the ledger's "Fix N invalid
 *  fields" — whichever tab is open, and the guard stays armed (the draft is still dirty).
 *
 *  - The rules (`validateItinerary`) run over the draft in edit mode; a BLOCKING issue is
 *    an invalid input of the record editor (Save off); every issue goes to the page's
 *    issues store, which the workspace's inputs read per record.
 *  - The record editor's errors on itinerary changes (a 400, a preview conflict) are
 *    published to the same store, so the inputs show them without reading the editor's
 *    context (which re-renders every consumer on every change). Renders nothing. */
import { useEffect, useMemo, useRef } from 'react';

import type { RecordKey } from '@/features/records/draft/changeSet';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { anchorKey } from '@/features/records/editor/choices';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import type { OutlineIssues } from '@/features/records/outline/issues';

import type { TourBaseline } from '../record/baselines';

import { DAYS_PATH, isItineraryRec, TOUR_KML } from './model';
import { issuesKey, validateItinerary } from './validation';

const split = (k: string): [RecordKey, string] => {
  const i = k.indexOf('|');
  return [k.slice(0, i), k.slice(i + 1)];
};

export function ItineraryRules({ baseline, issues }: { baseline: TourBaseline; issues: OutlineIssues }) {
  const { mode, setInvalidInput, errorsFor, invalidInputs } = useRecordEditor();
  const { state } = useDraft();
  const editing = mode === 'edit';
  const tourRec = baseline.rec;
  const list = useMemo(
    () => (editing ? validateItinerary(state.entries, baseline.paths, tourRec) : []),
    [editing, state.entries, baseline, tourRec],
  );
  const key = issuesKey(list);

  // The rules → the issues store and the editor's invalid inputs (diffed).
  const owned = useRef(new Map<string, string>());
  useEffect(() => {
    issues.setRules(list);
    const next = new Map<string, string>();
    for (const i of list) {
      if (!i.blocking) continue;
      const k = anchorKey(i.rec, i.path);
      next.set(k, next.has(k) ? `${next.get(k)} · ${i.message}` : i.message);
    }
    for (const k of owned.current.keys()) if (!next.has(k)) setInvalidInput(...split(k), null);
    for (const [k, m] of next) if (owned.current.get(k) !== m) setInvalidInput(...split(k), m);
    owned.current = next;
    // `key` stands for `list`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, issues, setInvalidInput]);
  useEffect(
    () => () => {
      for (const k of owned.current.keys()) setInvalidInput(...split(k), null);
      owned.current = new Map();
    },
    [setInvalidInput],
  );

  // The server's messages on itinerary changes → the issues store.
  useEffect(() => {
    const out = new Map<string, string[]>();
    for (const [rec, paths] of Object.entries(state.entries)) {
      const tour = rec === tourRec;
      if (!tour && !isItineraryRec(rec)) continue;
      for (const path of Object.keys(paths)) {
        if (tour && path !== DAYS_PATH && path !== TOUR_KML) continue;
        const own = invalidInputs[anchorKey(rec, path)];
        const mine = new Set(own ? own.split(' · ') : []);
        const msgs = errorsFor(rec, path).filter((m) => m !== own && !mine.has(m));
        if (msgs.length > 0) out.set(`${rec}|${path}`, msgs);
      }
    }
    issues.setServer(out);
  });

  return null;
}
